import React, { useEffect, useState, useCallback } from "react";
import {
  Label,
  Modal,
  ModalHeader,
  Col,
  Container,
  Row,
  ModalBody,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import "assets/scss/custom.scss";
import { useHolidayGuard } from "hooks/useHolidayGuard";
import PricingSection from "./UpdateOrder/PricingSection";
import SelectItemsOffers from "./UpdateOrder/CustomMenuList";
import ItemCard from "./UpdateOrder/ItemCard";
import OfferCard from "./UpdateOrder/OfferCard";
import Payment from "./Payment";
import {
  fetchOrdersForWaiter,
  removeItem,
  setToOrder,
  emptyOrder,
  updateOrderByOrderId,
} from "store/actions/orderActions";
import { clearTableData } from "store/actions/tableActions";
import CustomerData from "./UpdateOrder/CustomerData";
import { toast } from "react-toastify";
import {
  getCustomerByID,
  updateCustomerByID,
} from "store/actions/customerAction";
import ExtendTimeDropdown from "./UpdateOrder/ExtendTimeDropdown";
import { extendTableTime } from "store/actions/cartAction";

const UpdateOrderModal = ({ isOpen, toggler }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { restaurantsDetail } = useSelector((state) => state.restaurant);
  const { updatedOrder } = useSelector((state) => state.orders);
  const { updatedCustomerData } = useSelector((state) => state.customer);
  const waiterOrders = useSelector((state) => state.orders.waiterOrders);
  const { allTablesData } = useSelector((state) => state.table);
  const [selectedOrder, setSelectedOrder] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [extendedTime, setExtendedTime] = useState("");
  const [makePayment, setMakePayment] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [hasAnyItemAddon, setHasAnyItemAddon] = useState(false);

  const [customerData, setCustomerData] = useState({
    name: "",
    phoneNo: "",
    gender: "",
    alreadyExist: false,
  });
  const [fieldErrors, setFieldErros] = useState({
    name: "",
    phone: "",
    gender: "",
  });

  const [orderData, setOrderData] = useState({
    basePrice: 0,
    finalPrice: 0,
    appliedCharges: {},
  });

  const clearFieldsError = () => {
    setFieldErros({
      name: "",
      phone: "",
      gender: "",
    });
  };

  const restaurantID = user?.restaurantID;
  const bgColor = restaurantsDetail?.adminColor;
  const currency = restaurantsDetail?.currency || "$";

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchOrdersForWaiter(user?.id));
    }
  }, [isOpen, dispatch, user]);

  useEffect(() => {
    setSelectedOrder(updatedOrder);
    if (updatedOrder?.customerID !== "") {
      clearFieldsError();
      dispatch(getCustomerByID(updatedOrder?.customerID));
    }
    if (updatedOrder?.order?.some((item) => item.addons?.length > 0)) {
      setHasAnyItemAddon(true);
    } else {
      setHasAnyItemAddon(false);
    }
  }, [updatedOrder, dispatch]);

  useEffect(() => {
    setCustomerData((prev) => ({
      ...prev,
      name: updatedCustomerData?.name,
      phoneNo: updatedCustomerData?.contactNumber,
      gender: updatedCustomerData?.gender,
    }));
  }, [updatedCustomerData]);

  useEffect(() => {
    if (
      !restaurantsDetail ||
      !selectedOrder ||
      !Array.isArray(selectedOrder.order) ||
      selectedOrder.order.length === 0
    )
      return;

    const totalAmount = selectedOrder.order?.reduce((acc, item) => {
      const price = parseFloat(item.totalPrice) || 0;
      return acc + price;
    }, 0);

    //Base price value rounded to 2 digits
    const roundedTotal = parseFloat(totalAmount.toFixed(2));

    //Now calculating final price from applied charges
    let basePrice = parseFloat(totalAmount.toFixed(2));
    let final = basePrice;
    let charges = [];

    restaurantsDetail?.additionalCharges?.forEach((charge) => {
      if (charge.isActive) {
        const fee = Number(charge.fee) || 0;
        let addedAmount = charge.type === "fixed" ? fee : (final * fee) / 100;

        final += addedAmount;
        charges.push({
          name: charge.name || "Additional Charge",
          type: charge.type,
          fee,
          addedAmount: addedAmount.toFixed(2),
        });
      }
    });
    setOrderData(() => ({
      basePrice: roundedTotal,
      finalPrice: final,
      appliedCharges: charges,
    }));
  }, [selectedOrder, restaurantsDetail]);

  const { checkAndToast } = useHolidayGuard(restaurantsDetail, "dinein");
  const handlePay = (e) => {
    e.preventDefault();
    if (checkAndToast()) return;

    if (makePayment) {
      setShowPayment(true);
    } else {
      confirmOrder("", false);
    }
  };

  const tablesDropdown = useCallback(() => {
    if (!allTablesData?.allTables || !selectedOrder?.tableNumber) return [];

    const currentTime = new Date().getTime();

    const tableKey = Object.keys(allTablesData.allTables).find(
      (key) =>
        Number(allTablesData.allTables[key].tableNo) ===
        Number(selectedOrder.tableNumber)
    );

    const table = allTablesData.allTables[tableKey];

    let isExpired = false;
    let isAboutToExpire = false;

    if (table.expireAt) {
      const expiryMs = table.expireAt.toMillis();
      const warningStart = expiryMs - 5 * 60 * 1000; // 5 minutes before expiry

      isExpired = currentTime > expiryMs;
      isAboutToExpire = !isExpired && currentTime >= warningStart;
    }

    return [
      {
        ...table,
        isExpired,
        isAboutToExpire,
      },
    ];
  }, [allTablesData, selectedOrder]);

  const confirmOrder = async (paymentMethod = "", paymentRecieved = false) => {
    setIsLoading(true);
    const transformedOrder = selectedOrder.order.map(
      ({ orderItemID, value, isNew, ...item }) => {
        const { type, ...rest } = item;

        return {
          ...rest,
          ...(type && type !== "item" ? { type } : {}),
          price: Number(item.price) || 0,
          totalPrice: Number(item.totalPrice) || 0,
        };
      }
    );

    const appliedCharges = [...orderData.appliedCharges];
    const orderPayload = {
      appliedCharges,
      currency: currency || "",
      customerID: selectedOrder?.customerID || "",
      customerName: customerData?.name,

      order: transformedOrder,
      orderId: selectedOrder.orderId,

      orderType: "dinein",
      waiterID: user?.id || "nil",
      paymentMethod: paymentMethod,
      paymentRecieved: paymentRecieved,
      restaurantID: restaurantID || "nil",
      status:
        selectedOrder?.status === "served"
          ? paymentRecieved
            ? "completed"
            : "processing"
          : selectedOrder?.status,
      tableNumber: selectedOrder?.tableNumber,
      timer: selectedOrder?.timer || "",
      tip: null,
      tipType: "",
      totalAmount: Number(orderData?.basePrice) || 0, // or calculate if needed
      finalPrice: Number(orderData?.finalPrice) || 0,
    };

    const onSuccess = () => {
      dispatch(updateCustomerByID(updatedOrder?.customerID, customerData));
      toast.success("Order Updated successfully");
      dispatch(emptyOrder());
      toggler();
      setShowPayment(false);
      setIsLoading(false);

      if (extendedTime) {
        toast.info("Extending the table expiry time for this user");
        dispatch(
          extendTableTime(
            user?.restaurantID,
            selectedOrder?.customerID,
            selectedOrder?.tableNumber,
            allTablesData?.timer,
            restaurantsDetail?.name,
            extendedTime
          )
        );
      }

      if (paymentRecieved && !extendedTime) {
        dispatch(clearTableData(user.restaurantID, selectedOrder?.tableNumber));
      }
    };

    const onError = (errorMessage) => {
      toast.error("Error while updating order");
      setIsLoading(false);
    };

    await dispatch(updateOrderByOrderId(orderPayload, onSuccess, onError));
    return orderPayload.orderId;
  };

  const orderOptions = waiterOrders?.map((order) => {
    const itemNames = [];
    itemNames.push(`➡️ Status: ${order?.status}`);
    order.order?.forEach((item) => {
      if (item?.type === "offer") {
        itemNames.push(`🟡 Offer: ${item.name}`);
      } else if (item?.name) {
        itemNames.push(`🔹 Item: ${item.name}`);
      }
    });

    const labelSummary = itemNames.join(" • ");

    return {
      label: `Table no.${order.tableNumber} - ${labelSummary}`,
      value: order?.orderId,
      order: order,
    };
  });

  const handleSelectChange = (selectedOption) => {
    setSelectedOrder({});
    dispatch(setToOrder(selectedOption?.order));
    if (makePayment) setMakePayment(false);
  };

  const handleRemoveItem = (orderItemID) => {
    dispatch(removeItem(orderItemID));
  };

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const selectedOnlyItems = selectedOrder?.order?.filter(
    (item) => !item?.type || item?.type === "item"
  );
  const selectedOnlyOffers = selectedOrder?.order?.filter(
    (item) => item?.type === "offer"
  );

  const parseDuration = (durationStr) => {
    if (!durationStr) return 0;

    const lower = durationStr.toLowerCase();

    let minutes = 0;

    const hourMatch = lower.match(/(\d+)\s*(h|hr|hour|hours)/);
    if (hourMatch) {
      minutes += parseInt(hourMatch[1], 10) * 60;
    }

    const minMatch = lower.match(/(\d+)\s*(m|min|minute|minutes)/);
    if (minMatch) {
      minutes += parseInt(minMatch[1], 10);
    }

    return minutes;
  };

  const tableStatus = tablesDropdown()[0];

  const isEditableStatus = ["pending", "processing"].some(
    (st) => st === selectedOrder?.status
  );

  return (
    <Modal isOpen={isOpen} toggle={toggler} size="xl">
      <ModalHeader
        toggle={toggler}
        className="align-items-center border-bottom py-3"
      >
        Update Order
      </ModalHeader>

      {showPayment ? (
        <Payment
          setShowPayment={setShowPayment}
          totalAmount={orderData?.finalPrice || 0}
          confirmOrder={confirmOrder}
          loading={isLoading}
        />
      ) : (
        <ModalBody>
          <Container>
            <Row className="mb-3">
              <Col className="col-12">
                <Label className="mb-0">Select Order</Label>
                <Select
                  options={orderOptions}
                  onChange={handleSelectChange}
                  placeholder="Select an active order"
                  value={
                    orderOptions.find(
                      (opt) => opt.value === updatedOrder?.orderId
                    ) || null
                  }
                  isClearable
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                  menuPortalTarget={document.body} // <- very important
                />
              </Col>
            </Row>
            {selectedOrder?.order?.length > 0 && isEditableStatus && (
              <SelectItemsOffers
                currency={currency}
                restaurantID={restaurantID}
                selectedOrder={selectedOrder}
                setSelectedOrder={setSelectedOrder}
              />
            )}

            {selectedOrder?.order?.length > 0 ? (
              <Row className="w-100 mx-0">
                <Col className="col-12 mb-0 px-0 border-top border-bottom border-lighter">
                  {/* Row for Items */}
                  {selectedOnlyItems?.length > 0 && (
                    <Row className="p-0 mt-3">
                      {selectedOnlyItems.map((item) => (
                        <Col
                          key={item?.id}
                          xl={3}
                          lg={4}
                          md={6}
                          sm={6}
                          xs={12}
                          style={{ marginBottom: "19px" }}
                        >
                          <ItemCard
                            item={item}
                            handleRemoveItem={handleRemoveItem}
                            isEditableStatus={isEditableStatus}
                            hasAnyItemAddon={hasAnyItemAddon}
                          />
                        </Col>
                      ))}

                      <Col className="col-12">
                        <div
                          className={`w-100 ${
                            selectedOnlyOffers?.length > 0
                              ? "border-bottom"
                              : ""
                          }`}
                        ></div>
                      </Col>
                    </Row>
                  )}

                  {/* Row for Offers */}
                  {selectedOnlyOffers?.length > 0 && (
                    <Row className="p-0 mt-3">
                      {selectedOnlyOffers.map((item) => (
                        <Col
                          key={item?.id}
                          xl={3}
                          lg={4}
                          md={6}
                          sm={6}
                          xs={12}
                          style={{ marginBottom: "19px" }}
                        >
                          <OfferCard
                            offer={item}
                            handleRemoveItem={handleRemoveItem}
                            isEditableStatus={isEditableStatus}
                          />
                        </Col>
                      ))}
                    </Row>
                  )}
                </Col>

                <Col className="col-12">
                  <PricingSection
                    currency={currency || "$"}
                    finalPrice={orderData.finalPrice}
                    basePrice={orderData.basePrice}
                    appliedCharges={orderData.appliedCharges}
                  />
                </Col>

                <Col
                  xs="12"
                  className="col-12 d-flex align-items-center justify-content-between px-0 py-3 border-top border-lighter"
                >
                  <div className="d-flex align-items-center">
                    <input
                      type="checkbox"
                      id="proceedCheckbox"
                      className="me-2"
                      checked={makePayment}
                      onChange={(e) => setMakePayment(e.target.checked)}
                      disabled={selectedOrder?.status !== "served"} // ❗ Disable if not served
                      style={{
                        width: "15px",
                        height: "15px",
                        accentColor: bgColor,
                        cursor:
                          selectedOrder?.status !== "served"
                            ? "not-allowed"
                            : "pointer",
                      }}
                    />
                    <Label
                      for="proceedCheckbox"
                      className={`mb-0 ml-2 fs--14 ${
                        selectedOrder?.status !== "served"
                          ? "text-muted"
                          : "text-dark"
                      }`}
                    >
                      Proceed with Payment Now
                      {selectedOrder?.status !== "served" && (
                        <span className="text-muted fs--13">
                          {" "}
                          (Only allowed when order status is <b>"Served"</b>)
                        </span>
                      )}
                    </Label>
                  </div>
                  <span className="text-muted fs--13">
                    Status:&nbsp;
                    <b className={`status_${selectedOrder?.status}_color`}>
                      {capitalize(selectedOrder?.status)}
                    </b>
                  </span>
                </Col>

                <Col className="col-12">
                  <CustomerData
                    customerData={customerData}
                    setCustomerData={setCustomerData}
                    fieldErrors={fieldErrors}
                    setFieldErros={setFieldErros}
                    setIsValid={setIsValid}
                    bgColor={bgColor}
                    updatedCustomerData={updatedCustomerData}
                    previousPhoneNumber={updatedCustomerData?.contactNumber}
                    isEditableStatus={isEditableStatus}
                    clearFieldsError={clearFieldsError}
                  />
                </Col>

                <Col className="col-12 d-flex justify-content-center mt-4">
                  <Col xl={5} lg={7} md={9} sm={10} xs={12} className="">
                    {tableStatus?.isExpired && (
                      <div className="d-flex flex-column mb-3 align-items-center">
                        <div
                          className="d-flex align-items-center p-3 rounded"
                          style={{
                            backgroundColor: "#fff3cd",
                            border: "1px solid #ffeeba",
                            color: "#856404",
                            gap: "10px",
                          }}
                        >
                          <i
                            className="fas fa-info-circle"
                            style={{ fontSize: "1.4rem", flexShrink: 0 }}
                          ></i>
                          <div style={{ lineHeight: "1.4rem" }}>
                            Reservation for table{" "}
                            <b>{selectedOrder?.tableNumber}</b> has expired.
                          </div>
                        </div>
                        {["pending", "processing"].some(
                          (st) => st === selectedOrder?.status
                        ) && (
                          <ExtendTimeDropdown
                            maxDuration={
                              parseDuration(allTablesData?.timer) || 60
                            }
                            onSelect={(time) => setExtendedTime(time)}
                            bgColor={restaurantsDetail?.adminColor}
                          />
                        )}
                      </div>
                    )}
                    {tableStatus?.isAboutToExpire &&
                      !tableStatus?.isExpired && (
                        <div className="d-flex flex-column mb-3 align-items-center">
                          <div
                            className="d-flex align-items-center p-3 rounded"
                            style={{
                              backgroundColor: "#d1ecf1",
                              border: "1px solid #bee5eb",
                              color: "#0c5460",
                              gap: "10px",
                            }}
                          >
                            <i
                              className="fas fa-info-circle"
                              style={{ fontSize: "1.4rem", flexShrink: 0 }}
                            ></i>
                            <div style={{ lineHeight: "1.4rem" }}>
                              Reservation for table{" "}
                              <b>{selectedOrder?.tableNumber}</b> is about to
                              expire in 5 minutes. Consider extending the time.
                            </div>
                          </div>
                          <ExtendTimeDropdown
                            maxDuration={
                              parseDuration(allTablesData?.timer) || 60
                            }
                            onSelect={(time) => {
                              setExtendedTime(time);
                            }}
                            bgColor={restaurantsDetail?.adminColor}
                          />
                        </div>
                      )}
                  </Col>
                </Col>

                <Col className="col-12 d-flex justify-content-center px-0">
                  <Col className="col-12 col-sm-10 col-md-8 col-lg-5 col-xl-4 mt-3 px-0 d-flex justify-content-center">
                    <button
                      className="top_cards__addtocart fs--16 fw--500 w-100 btn text-white p-3"
                      style={{ backgroundColor: bgColor }}
                      onClick={handlePay}
                      disabled={
                        isLoading ||
                        !isValid ||
                        (!isEditableStatus &&
                          tableStatus?.isExpired &&
                          !makePayment)
                      }
                    >
                      {isLoading ? (
                        <Spinner size="sm" color="white" />
                      ) : (
                        `Confirm Update - ${currency || "$"}  ${
                          orderData?.finalPrice
                        }`
                      )}
                    </button>
                  </Col>
                </Col>
              </Row>
            ) : (
              <div className="w-100 d-flex flex-column justify-content-center align-items-center text-muted py-4 bg-secondary rounded">
                <i className="fas fa-box-open fs--24 mb-2" />
                <div className="fs--16 fw--400">No Order Selected Yet</div>
              </div>
            )}
          </Container>
        </ModalBody>
      )}
    </Modal>
  );
};

export default UpdateOrderModal;
